/**
 * Users List Page
 *
 * Team member management for merchants.
 * Allows admins to invite, edit, and remove users.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  X,
  Mail,
  MapPin,
  Clock,
} from 'lucide-react';
import { useAuth, type RoleType } from '../contexts/AuthContext';
import { getMerchantById } from '../services/merchants.service';
import {
  getUsers,
  inviteUser,
  updateUserRole,
  updateUserProfile,
  removeUserFromMerchant,
  type UserWithRole,
} from '../services/users.service';
import { getLocations } from '../services/locations.service';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { useDateFormatter } from '../hooks/useDateFormatter';
import type { Database } from '../lib/database.types';

type Location = Database['public']['Tables']['locations']['Row'];

const ROLE_LABELS: Record<RoleType, string> = {
  admin: 'Admin',
  manager: 'Manager',
  operator: 'Operator',
  viewer: 'Viewer',
  equipment_check: 'Equipment check',
};

const ROLE_DESCRIPTIONS: Record<RoleType, string> = {
  admin: 'Full access to all locations and settings',
  manager: 'Manage assigned locations',
  operator: 'Manage bookings, issues & maintenance',
  viewer: 'Read-only access',
  equipment_check: 'Inspect returned equipment and prepare for next handover',
};

const ROLE_COLORS: Record<RoleType, string> = {
  admin: 'badge-info',
  manager: 'badge-success',
  operator: 'badge-warning',
  viewer: 'badge-secondary',
  equipment_check: 'badge-secondary',
};

// Converts a snake_case role value into the suffix used in i18n keys
// e.g. 'admin' -> 'Admin', 'equipment_check' -> 'EquipmentCheck'.
const roleKeySuffix = (r: string): string =>
  r.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');

export default function UsersList() {
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin, user } = useAuth();
  const { formatDateTimeCompact } = useDateFormatter();

  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [equipmentCheckEnabled, setEquipmentCheckEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleType | 'all'>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);

  useEffect(() => {
    loadData();
  }, [merchantId, isPlatformAdmin]);

  async function loadData() {
    setLoading(true);
    setError(null);

    // Load users and locations in parallel
    const [usersResult, locationsResult] = await Promise.all([
      getUsers({ merchantId, locationIds, isPlatformAdmin }),
      getLocations({ merchantId, locationIds, isPlatformAdmin }),
    ]);

    if (usersResult.error) {
      setError(typeof usersResult.error === 'string' ? usersResult.error : t('users.errorLoading'));
      setLoading(false);
      return;
    }

    if (usersResult.data) {
      setUsers(usersResult.data);
    }

    if (locationsResult.data) {
      setLocations(locationsResult.data);
    }

    if (merchantId) {
      const { data: merchant } = await getMerchantById(merchantId);
      setEquipmentCheckEnabled(((merchant as any)?.equipment_check_enabled) === true);
    }

    setLoading(false);
  }

  // Filter users
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      !searchQuery ||
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = roleFilter === 'all' || u.role === roleFilter;

    const isMerchantWide = !u.location_ids || u.location_ids.length === 0;
    const matchesLocation =
      locationFilter === 'all' ||
      (locationFilter === 'all-locations' && isMerchantWide) ||
      (!isMerchantWide && u.location_ids!.includes(locationFilter));

    return matchesSearch && matchesRole && matchesLocation;
  });

  const handleEditUser = (u: UserWithRole) => {
    setSelectedUser(u);
    setShowEditModal(true);
  };

  const handleDeleteUser = (u: UserWithRole) => {
    setSelectedUser(u);
    setShowDeleteDialog(true);
  };

  const formatLastLogin = (timestamp: string | null | undefined) => {
    if (!timestamp) return t('users.never');
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return t('users.justNow');
    if (diffHours < 24) return t('users.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('users.daysAgo', { count: diffDays });
    return formatDateTimeCompact(date);
  };

  const getInitials = (name: string | null, email: string | null | undefined) => {
    if (name) {
      const parts = name.split(' ');
      return parts.length > 1
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        : name.substring(0, 2).toUpperCase();
    }
    return email ? email.substring(0, 2).toUpperCase() : '??';
  };

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('users.title')}</h1>
          <p className="text-secondary">{t('users.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button onClick={() => setShowInviteModal(true)} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            {t('users.inviteUser')}
          </button>
        </div>
      </div>

      {/* Main Card */}
      <div className="card">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-lg mb-xl">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleType | 'all')}
            className="input md:w-40"
          >
            <option value="all">{t('users.allRoles')}</option>
            <option value="admin">{t('users.roleAdmin')}</option>
            <option value="manager">{t('users.roleManager')}</option>
            <option value="operator">{t('users.roleOperator')}</option>
            <option value="viewer">{t('users.roleViewer')}</option>
          </select>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="input md:w-48"
          >
            <option value="all">{t('users.allLocations')}</option>
            <option value="all-locations">{t('users.merchantWideAccess')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.location_name}
              </option>
            ))}
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
              <Users className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('users.errorLoading')}</h3>
            <p className="text-secondary mb-xl">{error}</p>
            <button onClick={loadData} className="btn btn-primary">
              {t('common.retry')}
            </button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
              <Users className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('users.noUsers')}</h3>
            <p className="text-secondary mb-xl">
              {searchQuery || roleFilter !== 'all' || locationFilter !== 'all'
                ? t('users.tryAdjusting')
                : t('users.inviteHint')}
            </p>
            {!searchQuery && roleFilter === 'all' && locationFilter === 'all' && (
              <button onClick={() => setShowInviteModal(true)} className="btn btn-primary">
                <Plus className="w-4 h-4" />
                {t('users.inviteUser')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary">
                  <th className="text-left py-md px-sm font-medium text-secondary">{t('users.tableUser')}</th>
                  <th className="text-left py-md px-sm font-medium text-secondary">{t('users.role')}</th>
                  <th className="text-left py-md px-sm font-medium text-secondary hidden md:table-cell">
                    {t('users.locationAccess')}
                  </th>
                  <th className="text-left py-md px-sm font-medium text-secondary hidden lg:table-cell">
                    {t('users.lastLogin')}
                  </th>
                  <th className="text-right py-md px-sm font-medium text-secondary">{t('users.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-primary hover:bg-tertiary/50">
                    <td className="py-md px-sm">
                      <div className="flex items-center gap-md">
                        {u.avatar_url ? (
                          <img
                            src={u.avatar_url}
                            alt={u.full_name || 'User'}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue/10 flex items-center justify-center text-blue font-medium">
                            {getInitials(u.full_name, u.email)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-primary">{u.full_name || t('users.noName')}</p>
                          <p className="text-sm text-secondary">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-md px-sm">
                      <span className={`badge ${ROLE_COLORS[u.role]}`}>{t(`users.role${roleKeySuffix(u.role)}`)}</span>
                    </td>
                    <td className="py-md px-sm hidden md:table-cell">
                      <div className="flex items-center gap-xs text-secondary">
                        <MapPin className="w-4 h-4" />
                        <span title={u.location_names && u.location_names.length > 0 ? u.location_names.join(', ') : undefined}>
                          {!u.location_ids || u.location_ids.length === 0
                            ? t('users.allLocations')
                            : u.location_ids.length === 1
                              ? (u.location_names?.[0] || t('users.allLocations'))
                              : t('users.nLocations', { count: u.location_ids.length })}
                        </span>
                      </div>
                    </td>
                    <td className="py-md px-sm hidden lg:table-cell">
                      <div className="flex items-center gap-xs text-secondary">
                        <Clock className="w-4 h-4" />
                        <span>{formatLastLogin(u.last_sign_in_at)}</span>
                      </div>
                    </td>
                    <td className="py-md px-sm">
                      <div className="flex items-center justify-end gap-sm">
                        <button
                          onClick={() => handleEditUser(u)}
                          className="btn btn-ghost btn-sm"
                          title={t('users.editUser')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u)}
                          className="btn btn-ghost btn-sm text-danger hover:bg-danger/10"
                          title={t('users.removeUser')}
                          disabled={u.id === user?.id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="mt-lg pt-lg border-t border-primary flex items-center justify-between">
          <p className="text-sm text-secondary">
            {t('users.showingCount', { filtered: filteredUsers.length, total: users.length })}
          </p>
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          merchantId={merchantId!}
          locations={locations}
          equipmentCheckEnabled={equipmentCheckEnabled}
          onSuccess={() => {
            setShowInviteModal(false);
            loadData();
          }}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          locations={locations}
          equipmentCheckEnabled={equipmentCheckEnabled}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            loadData();
          }}
        />
      )}

      {/* Delete User Dialog */}
      {showDeleteDialog && selectedUser && (
        <DeleteUserDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          currentUserId={user?.id || ''}
          onSuccess={() => {
            setShowDeleteDialog(false);
            setSelectedUser(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

// Returns true if two location-id selections represent the same set.
function sameLocationSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

// Multi-select location picker. An empty selection means "all locations".
function LocationMultiSelect({
  locations,
  selectedIds,
  onChange,
}: {
  locations: Location[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const allSelected = selectedIds.length === 0;

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-xs max-h-56 overflow-y-auto">
      <label
        className={`flex items-center gap-sm p-sm border rounded-lg cursor-pointer transition-colors ${
          allSelected ? 'border-blue bg-blue/5' : 'border-primary hover:border-secondary'
        }`}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => onChange([])}
        />
        <span className="text-sm font-medium text-primary">{t('users.allLocations')}</span>
      </label>
      {locations.map((loc) => {
        const checked = selectedIds.includes(loc.id);
        return (
          <label
            key={loc.id}
            className={`flex items-center gap-sm p-sm border rounded-lg cursor-pointer transition-colors ${
              checked ? 'border-blue bg-blue/5' : 'border-primary hover:border-secondary'
            }`}
          >
            <input type="checkbox" checked={checked} onChange={() => toggle(loc.id)} />
            <span className="text-sm text-primary">{loc.location_name}</span>
          </label>
        );
      })}
    </div>
  );
}

// Invite User Modal Component
function InviteUserModal({
  isOpen,
  onClose,
  merchantId,
  locations,
  equipmentCheckEnabled,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  locations: Location[];
  equipmentCheckEnabled: boolean;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleType>('viewer');
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('users.invalidEmail'));
      setLoading(false);
      return;
    }

    const { error: inviteError } = await inviteUser(
      email,
      merchantId,
      role,
      role === 'admin' ? null : (locationIds.length > 0 ? locationIds : null)
    );

    if (inviteError) {
      setError(typeof inviteError === 'string' ? inviteError : t('users.inviteFailed'));
      setLoading(false);
      return;
    }

    onSuccess();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="rounded-lg shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ backgroundColor: '#f2f2f2', maxHeight: '90vh', maxWidth: '448px' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-white rounded-t-lg flex-shrink-0">
          <div className="flex items-center gap-sm sm:gap-md">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue flex-shrink-0" />
            <h2 className="text-lg sm:text-xl font-bold text-primary">{t('users.inviteTeamMember')}</h2>
          </div>
          <button onClick={onClose} className="btn-icon flex-shrink-0" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - scrollable content with fixed footer */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-md sm:p-lg space-y-lg">
            {error && (
              <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('users.emailAddress')} <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('users.role')} <span className="text-danger">*</span>
              </label>
              <div className="space-y-sm">
                {([
                  'admin', 'manager', 'operator', 'viewer',
                  ...(equipmentCheckEnabled ? ['equipment_check'] : []),
                ] as RoleType[]).map((r) => (
                  <label
                    key={r}
                    className={`flex items-start gap-sm sm:gap-md p-sm sm:p-md border rounded-lg cursor-pointer transition-colors ${
                      role === r ? 'border-blue bg-blue/5' : 'border-primary hover:border-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={r}
                      checked={role === r}
                      onChange={() => setRole(r)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-primary text-sm sm:text-base">{t(`users.role${roleKeySuffix(r)}`)}</p>
                      <p className="text-xs sm:text-sm text-secondary">{t(`users.role${roleKeySuffix(r)}Desc`)}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {role !== 'admin' && (
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('users.locationAccess')}
                </label>
                <LocationMultiSelect
                  locations={locations}
                  selectedIds={locationIds}
                  onChange={setLocationIds}
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('users.locationAccessHintMulti')}
                </p>
              </div>
            )}
          </div>

          {/* Footer - always visible */}
          <div className="flex items-center justify-end gap-sm sm:gap-md p-md sm:p-lg border-t border-primary flex-shrink-0">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('users.sending')}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  {t('users.sendInvitation')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// Edit User Modal Component
function EditUserModal({
  isOpen,
  onClose,
  user,
  locations,
  equipmentCheckEnabled,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithRole;
  locations: Location[];
  equipmentCheckEnabled: boolean;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(user.full_name || '');
  const [role, setRole] = useState<RoleType>(user.role);
  const [locationIds, setLocationIds] = useState<string[]>(user.location_ids || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Update profile if changed
    if (fullName !== user.full_name) {
      const { error: profileError } = await updateUserProfile(user.id, {
        full_name: fullName,
      });

      if (profileError) {
        setError(typeof profileError === 'string' ? profileError : t('users.updateProfileFailed'));
        setLoading(false);
        return;
      }
    }

    // Update role if changed
    const currentLocationIds = user.location_ids || [];
    const nextLocationIds = role === 'admin' ? [] : locationIds;
    if (role !== user.role || !sameLocationSet(nextLocationIds, currentLocationIds)) {
      const { error: roleError } = await updateUserRole(user.role_id, {
        role,
        location_ids: nextLocationIds.length > 0 ? nextLocationIds : null,
      });

      if (roleError) {
        setError(typeof roleError === 'string' ? roleError : t('users.updateRoleFailed'));
        setLoading(false);
        return;
      }
    }

    onSuccess();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="rounded-lg shadow-2xl max-w-md w-full"
        style={{ backgroundColor: '#f2f2f2' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-lg border-b border-primary bg-white rounded-t-lg">
          <div className="flex items-center gap-md">
            <Edit className="w-6 h-6 text-blue" />
            <h2 className="text-xl font-bold text-primary">{t('users.editUser')}</h2>
          </div>
          <button onClick={onClose} className="btn-icon" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-lg space-y-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-secondary mb-xs">{t('users.email')}</label>
            <p className="text-primary">{user.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('users.fullName')}</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('users.role')}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleType)}
              className="input w-full"
            >
              <option value="admin">{t('users.roleAdmin')}</option>
              <option value="manager">{t('users.roleManager')}</option>
              <option value="operator">{t('users.roleOperator')}</option>
              <option value="viewer">{t('users.roleViewer')}</option>
              {(equipmentCheckEnabled || user.role === 'equipment_check') && (
                <option value="equipment_check">{t('users.roleEquipmentCheck')}</option>
              )}
            </select>
          </div>

          {role !== 'admin' && (
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('users.locationAccess')}
              </label>
              <LocationMultiSelect
                locations={locations}
                selectedIds={locationIds}
                onChange={setLocationIds}
              />
              <p className="text-xs text-secondary mt-xs">
                {t('users.locationAccessHintMulti')}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-md pt-lg border-t border-primary">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('users.saveChanges')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// Delete User Dialog Component
function DeleteUserDialog({
  isOpen,
  onClose,
  user,
  currentUserId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithRole;
  currentUserId: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const { error: deleteError } = await removeUserFromMerchant(user.role_id, currentUserId);

    if (deleteError) {
      setError(typeof deleteError === 'string' ? deleteError : t('users.removeFailed'));
      setLoading(false);
      return;
    }

    onSuccess();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="rounded-lg shadow-2xl max-w-md w-full"
        style={{ backgroundColor: '#f2f2f2' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-lg border-b border-primary bg-white rounded-t-lg">
          <div className="flex items-center gap-md">
            <Trash2 className="w-6 h-6 text-danger" />
            <h2 className="text-xl font-bold text-primary">{t('users.removeUser')}</h2>
          </div>
          <button onClick={onClose} className="btn-icon" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <p className="text-primary mb-md">
            {t('users.removeConfirm', { name: user.full_name || user.email })}
          </p>
          <p className="text-secondary text-sm">
            {t('users.removeWarning')}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-md p-lg border-t border-primary">
          <button type="button" onClick={onClose} className="btn btn-ghost">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="btn btn-danger"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('users.removing')}
              </>
            ) : (
              t('users.removeUser')
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
